import { Select, Spin, Tag } from 'antd';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { badValueValidation } from '../../../../modules/crm/inPatient/service/crmUtils';
import { convertToValidStr } from '../../../../utils/utility';
import { KEYS_TO_HANDLE, MAX_CHARACTERS_LIMIT, ONLY_ALPHABET, ONLY_ALPHA_NUMERIC } from '../../../constants/commonConstants';
import { useGetFormConfig } from '../../../hooks/useFormConfig';
import FormItem from '../FormItem/FormItem';
import './formSelect.less';

const { Option } = Select;

const FormSelect = (props: any): (ReactElement | null) => {
  const [searchValue, setSearchValue] = useState('');
  const [extraRules, setExtraRules] = useState<any[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const selectRef = useRef<any>(null);

  const formConfig = useGetFormConfig(props);
  const {
    disabled, className, showSearch, mode, onChange: onChangeFromOrigin, onSearch: onSearchOrigin, defaultValue,
    customOption: CustomOption, isOptionsLoading, maxTagCount, key, allowClear, isBadValueCheck, badValues, onlyAlphaNumeric,
    allowOptionDisabled, disabledReason, isEmailVerified, removeOther, maxLengthValue, fieldLengthValue, onlyAlpha, dropDownRenderReq,
    suffixIcon, onSelect, isGroupLogin, onPopupScroll, onPanelLeave, isRedesign, onBlur, onFocus,
  } = formConfig;
  const dynamicPlaceholder =
    props?.detail?.placeholder !== undefined
    ? props.detail.placeholder      // Respect the dynamic placeholder from props
    : formConfig?.placeholder;      // Otherwise, fallback to formConfig placeholder

  let { options = [] } = formConfig;

  useEffect((): any => {
    if ((key === 'hospitalCenter' || key === 'grouplogincenter') && !onPanelLeave) {
      setIsFocused(onPanelLeave);
    }
  }, [onPanelLeave]);

  const badValueRule = useMemo((): any => (isBadValueCheck ? [
    badValueValidation(key, badValues),
  ] : []), [isBadValueCheck]);

  const onSearch = (event: any): void => {
    let validStr = convertToValidStr(event);
    if (onlyAlphaNumeric) {
      validStr = validStr.replace(ONLY_ALPHA_NUMERIC, '');
    }
    if (onlyAlpha) {
      validStr = validStr?.replace(ONLY_ALPHABET, '');

    }
    setSearchValue(maxLengthValue ? validStr.slice(0, fieldLengthValue[0]) : validStr?.length >= MAX_CHARACTERS_LIMIT ?
      validStr.slice(0, MAX_CHARACTERS_LIMIT) : validStr);
    if (onSearchOrigin) {
      onSearchOrigin(validStr);
    }
  };

  const onChange = (event: any): void => {
    setSearchValue('');
    setExtraRules([]);
    if (onChangeFromOrigin) {
      onChangeFromOrigin(event);
    }
    if ((key === 'hospitalCenter' || key === 'grouplogincenter')) {
      setIsFocused(false);
    }
  };

  const handleDropdownVisibleChange = (e: any): any => {
    e?.selectRef?.current?.scrollTo?.(0);
  };

  const onMouseLeave = (event: any): any => {
    if (!event?.currentTarget?.contains(event?.relatedTarget)) {
      setIsFocused(false);
    }
  };
  const onMouseEnter = (e: any): void => {
    setIsFocused(true);
  };

  const selectAll = (): void => {
    setSearchValue('');
    setExtraRules([]);
    options.forEach((option: any): any => {
      tagRender({tagLabel: option.name, closable: true, value: option.value});

    });
    if (onChangeFromOrigin) {
      onChangeFromOrigin(options.map((option: any): any => option.key));
    }
  };

  const clearAll = (): void => {
    if (onChangeFromOrigin) {
      onChangeFromOrigin([]);
    }
  };

  const inputOnKeyDown = (event: any): any => {
    setIsFocused(true);
    if (event.key === KEYS_TO_HANDLE?.Enter) {
      setIsFocused(false);
    }
  };

  // TODO Add max width in tags
  function tagRender(tagProps: any): ReactElement {
    const { label: tagLabel, closable, onClose } = tagProps;
    return (
      <Tag closable={closable} onClose={onClose} >
        <span className="tag-name-length">
          {tagLabel}
        </span>
      </Tag>
    );
  }

  if (removeOther) {
    options = options?.filter((ele: any): boolean => (
      ele !== 'Other' &&
      ele !== 'Others' &&
      ele?.label !== 'Others' &&
      ele?.label !== 'Other'
    ));
  }

  return <FormItem formConfig={formConfig} extraRules={[...extraRules, ...badValueRule]}>
  <Select
    allowClear={allowClear}
    data-testid={key}
    ref={selectRef}
    className= {className}
    defaultValue={defaultValue}
    disabled={disabled}
    notFoundContent={isOptionsLoading ? <Spin size="small" /> : null}
    showSearch={(onSearchOrigin === true || onSearchOrigin === false) ? onSearchOrigin : showSearch}
    placeholder={dynamicPlaceholder}
    onKeyDown={(key === 'hospitalCenter' || key === 'grouplogincenter') ? inputOnKeyDown : undefined}
    tagRender={tagRender}
    mode={mode}
    maxTagCount={maxTagCount}
    onChange={(option: any): void => onChange(option)}
    onSearch={(event: any): any => onSearch(event)}
    onSelect = {onSelect}
    open={(key === 'hospitalCenter' || key === 'grouplogincenter') ? isFocused : undefined}
    optionFilterProp="children"
    searchValue={searchValue}
    suffixIcon = {suffixIcon}
    onPopupScroll={onPopupScroll}
    onMouseDown={(key === 'hospitalCenter' || key === 'grouplogincenter') ? onMouseEnter : undefined}
    onDropdownVisibleChange={(e: any): any => handleDropdownVisibleChange(selectRef?.current)}
    onBlur={(): any => {
      setSearchValue('');
      onBlur && onBlur();
    }}
    onFocus={(): void => onFocus && onFocus()}
    dropdownClassName={isRedesign ? 'his-redesign-Dropdown' : ''}
    dropdownRender={(menu: any): any => (
      <div className={`menu-class ${isRedesign ? 'Redesign-Dropdown' : ''}`} onMouseLeave={(key === 'hospitalCenter' || key === 'grouplogincenter') ? onMouseLeave : undefined}>
        {menu}
        {
          dropDownRenderReq &&
          <div className="bottom-menu">
            <a className="text-underline btn-link" onClick={(): any => selectAll()}>Select All</a>
            <a className="text-underline btn-link" onClick={(): any => clearAll()}>Clear All</a>
          </div>
        }
      </div>
    )}
    filterOption={CustomOption ? false : (input: string, option: any): boolean =>
      ((option?.children || '').toString().toLowerCase().indexOf(input?.toLowerCase()) >= 0)
    }>
    {/* <Option key={"all"} value={"all"}>Select all</Option> */}
    {
        options?.map((option: any): ReactElement => {
          let name = option;
          let value = option;
          if (option?.value && option?.name) {
            name = option?.name;
            value = option?.value || option?.name;
          }
          return (

            !isEmailVerified && option?.isEmailNotConfigured ?
              option?.customComponent
              :
          !option?.disabled ?
          <Option key={value} value={value} title={name} data-testid={`${key}-${value}`} className={isGroupLogin ? 'select-options-center' :
          isRedesign ? 'NA-Redesign-Dropdown-Options' : ''}>
            {
              CustomOption ? <CustomOption {...option} /> : name
            }
          </Option> :
              (
                allowOptionDisabled ?
                    <Option key={value} value={value} disabled={true} title={disabledReason || name} className={!disabledReason ? '' : 'error-text'}>
                      {
                        CustomOption ? <CustomOption {...option} /> : name
                      }
                    </Option> :
                    <Option className="clr-disabled mouse-not-allowed" key={value}
                      value={value} title={name} data-testid={`${key}-${value}`}>
                      {name}
                    </Option>
                )
          );
        })
      }
    </Select>
  </FormItem>;
};

export default FormSelect;
